import React, { useEffect, useRef, useState } from 'react';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';

import FilterIcon from 'Assets/icons/filter.svg';

const normaliseName = (name) => String(name).trim().toLowerCase();

/**
 * One Filter control: exclude sources (by key) and first-level categories (by name, across every
 * source). Exclusions that no longer match an option are ignored by the server and not counted.
 */
const RoadmapFilterControl = ({ options, excludedSources, excludedCategories, onChange }) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const sourceOptions = options?.sources || [];
    const categoryOptions = options?.categories || [];

    const excludedSourceSet = new Set(excludedSources);
    const excludedCategorySet = new Set(excludedCategories.map(normaliseName));

    const activeCount =
        sourceOptions.filter((source) => excludedSourceSet.has(source.key)).length +
        categoryOptions.filter((category) => excludedCategorySet.has(normaliseName(category))).length;

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const closeOnOutsideClick = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);

        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [open]);

    const toggleSource = (key, checked) => {
        const next = excludedSources.filter((excluded) => excluded !== key);

        onChange({ excludedSources: checked ? [...next, key] : next });
    };

    const toggleCategory = (name, checked) => {
        const next = excludedCategories.filter((excluded) => normaliseName(excluded) !== normaliseName(name));

        onChange({ excludedCategories: checked ? [...next, name] : next });
    };

    return (
        <div ref={containerRef} className="tw-relative">
            <Button variant="secondary" small aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((isOpen) => !isOpen)}>
                <FilterIcon className="tw-h-4 tw-w-4 tw-me-1.5" aria-hidden="true" />
                {$t('Filter')}
                {activeCount > 0 && (
                    <span className="tw-ms-1.5 tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-full tw-bg-blue tw-text-2xs tw-font-semibold tw-text-white">
                        {activeCount}
                    </span>
                )}
            </Button>

            {open && (
                <div
                    role="dialog"
                    aria-label={$t('Filter')}
                    className="tw-absolute tw-top-full tw-start-0 tw-z-50 tw-mt-2 tw-w-[300px] tw-max-h-[60vh] tw-overflow-y-auto tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-p-4 tw-shadow-primary"
                >
                    <div className="tw-space-y-4">
                        <fieldset>
                            <legend className="tw-mb-2 tw-text-xs tw-font-semibold tw-text-charcoal-75">{$t('Exclude sources')}</legend>
                            <div className="tw-space-y-1.5">
                                {sourceOptions.map((source) => (
                                    <CheckboxInput
                                        key={source.key}
                                        id={`roadmap-exclude-source-${source.key}`}
                                        label={source.name}
                                        checked={excludedSourceSet.has(source.key)}
                                        onChange={(event) => toggleSource(source.key, event.target.checked)}
                                    />
                                ))}
                            </div>
                        </fieldset>

                        <fieldset>
                            <legend className="tw-mb-2 tw-text-xs tw-font-semibold tw-text-charcoal-75">{$t('Exclude categories')}</legend>
                            <div className="tw-space-y-1.5">
                                {categoryOptions.map((category) => (
                                    <CheckboxInput
                                        key={category}
                                        id={`roadmap-exclude-category-${normaliseName(category)}`}
                                        label={category}
                                        checked={excludedCategorySet.has(normaliseName(category))}
                                        onChange={(event) => toggleCategory(category, event.target.checked)}
                                    />
                                ))}
                            </div>
                        </fieldset>

                        <div className="tw-flex tw-justify-end tw-gap-2">
                            <Button variant="secondary" small onClick={() => onChange({ excludedSources: [], excludedCategories: [] })}>
                                {$t('Clear')}
                            </Button>
                            <Button variant="primary" small onClick={() => setOpen(false)}>
                                {$t('Done')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoadmapFilterControl;
